
import { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    propertyLocation: '',
    propertyType: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('propertyLocation', formData.propertyLocation);
      body.append('propertyType', formData.propertyType);
      body.append('message', formData.message);

      const res = await fetch('https://readdy.ai/api/form/d6h4ffhtp4hj31tchve0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitStatus('success');
        window.REACT_APP_NAVIGATE('/thank-you');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 relative overflow-hidden">
      {/* 和紙風背景 */}
      <div className="absolute inset-0 bg-gradient-to-br from-stone-900 via-slate-800 to-stone-900" />
      <div
        className="absolute inset-0 bg-cover bg-center opacity-10"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/fccab9e36afaa3a7fff3d4497cc41ec2.jpg')`,
        }}
      />

      {/* 上部装飾ライン */}
      <div className="absolute top-0 left-0 right-0 h-3 bg-gradient-to-r from-transparent via-[#8b2500] to-transparent opacity-80" />
      <div className="absolute top-3 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" />

      {/* 市松模様装飾 */}
      <div className="absolute top-8 left-0 right-0 flex justify-center gap-1">
        {[...Array(20)].map((_, i) => (
          <div key={i} className={`w-3 h-3 ${i % 2 === 0 ? 'bg-[#8b2500]' : 'bg-[#c9a84c]'} opacity-60`} />
        ))}
      </div>

      {/* 背景装飾円 */}
      <div className="absolute -left-32 -top-32 w-96 h-96 rounded-full bg-[#c9a84c]/10" />
      <div className="absolute -right-32 -bottom-32 w-96 h-96 rounded-full bg-[#c9a84c]/10" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ヘッダー */}
        <div className="text-center mb-14">
          <span
            className="inline-flex items-center gap-2 bg-[#8b2500]/80 text-[#c9a84c] text-xs font-bold tracking-widest uppercase px-5 py-2 rounded-full mb-4 border border-[#c9a84c]/30"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            <i className="ri-mail-line text-base"></i>
            Contact
          </span>
          <h2
            className="text-4xl sm:text-5xl font-extrabold text-white mb-4"
            style={{ fontFamily: "'Noto Serif JP', serif", letterSpacing: '0.05em' }}
          >
            <span className="text-[#c9a84c]">◆</span>　無料相談・お問い合わせ　<span className="text-[#c9a84c]">◆</span>
          </h2>
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#c9a84c]" />
            <span className="text-[#c9a84c] text-sm">✦</span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#c9a84c]" />
          </div>
          <p className="text-lg text-slate-300" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            物件の状況をお聞きし、最適なプランをご提案します
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">

          {/* 左：情報カード */}
          <div className="lg:col-span-2 flex flex-col gap-6">

            {/* Why Choose Us */}
            <div className="bg-white/8 backdrop-blur-sm border-2 border-[#c9a84c]/30 rounded-2xl p-7 relative">
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#c9a84c]" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#c9a84c]" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#c9a84c]" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#c9a84c]" />

              <h3
                className="text-lg font-bold text-white mb-6 flex items-center gap-3"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-[#8b2500] to-[#a52a00] rounded-xl flex-shrink-0 shadow-lg">
                  <i className="ri-lightbulb-line text-[#c9a84c] text-lg"></i>
                </div>
                <span>選ばれる理由</span>
              </h3>

              <ul className="space-y-4">
                {[
                  { icon: 'ri-checkbox-circle-line', text: '初回相談は完全無料' },
                  { icon: 'ri-checkbox-circle-line', text: '東京エリア実績多数' },
                  { icon: 'ri-checkbox-circle-line', text: '2営業日以内にご返信' },
                  { icon: 'ri-checkbox-circle-line', text: '現役ホストが直接対応' },
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 border-l-2 border-[#8b2500] pl-3">
                    <i className={`${item.icon} text-[#c9a84c] text-xl flex-shrink-0`}></i>
                    <span className="text-slate-300 text-sm" style={{ fontFamily: "'Noto Serif JP', serif" }}>{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* こんな方におすすめ */}
            <div className="bg-white/8 backdrop-blur-sm border-2 border-[#c9a84c]/30 rounded-2xl p-7 relative">
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#c9a84c]" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#c9a84c]" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#c9a84c]" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#c9a84c]" />

              <h3
                className="text-lg font-bold text-white mb-6 flex items-center gap-3"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-[#8b2500] to-[#a52a00] rounded-xl flex-shrink-0 shadow-lg">
                  <i className="ri-user-search-line text-[#c9a84c] text-lg"></i>
                </div>
                <span>こんな方におすすめ</span>
              </h3>

              <ul className="space-y-3">
                {[
                  '民泊・簡易宿所を始めたい',
                  '申請手続きのサポートが必要',
                  '運営を丸ごと任せたい',
                  '清掃業者を探している',
                  '収益を最大化したい',
                ].map((text, i) => (
                  <li key={i} className="flex items-start gap-2 border-l-2 border-[#c9a84c] pl-3">
                    <i className="ri-arrow-right-s-line text-[#c9a84c] flex-shrink-0 mt-0.5"></i>
                    <span className="text-slate-300 text-sm" style={{ fontFamily: "'Noto Serif JP', serif" }}>{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* 信頼バッジ */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '100%', label: '無料相談' },
                { value: '2日', label: '以内に返信' },
              ].map((item, i) => (
                <div key={i} className="bg-[#8b2500]/40 border-2 border-[#c9a84c]/40 rounded-xl p-5 text-center relative">
                  <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-[#c9a84c]" />
                  <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-[#c9a84c]" />
                  <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-[#c9a84c]" />
                  <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[#c9a84c]" />
                  <div className="text-3xl font-extrabold text-[#c9a84c] mb-1" style={{ fontFamily: "'Noto Serif JP', serif" }}>{item.value}</div>
                  <div className="text-xs text-slate-300" style={{ fontFamily: "'Noto Serif JP', serif" }}>{item.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* 右：フォーム */}
          <div className="lg:col-span-3">
            <div
              className="rounded-2xl shadow-2xl p-8 md:p-10 border-4 border-[#8b2500] relative"
              style={{ background: 'linear-gradient(135deg, #fff8e1 0%, #fef3e2 50%, #fff8e1 100%)' }}
            >
              <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-[#c9a84c]" />
              <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-[#c9a84c]" />
              <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-[#c9a84c]" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-[#c9a84c]" />

              <h3
                className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-[#8b2500] to-[#a52a00] rounded-xl shadow-lg">
                  <i className="ri-mail-send-line text-[#c9a84c] text-lg"></i>
                </div>
                無料相談フォーム
              </h3>

              <form
                id="for-owners-contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                {/* お名前 */}
                <div>
                  <label
                    className="flex items-center justify-between text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide"
                    style={{ fontFamily: "'Noto Serif JP', serif" }}
                  >
                    <span>お名前 / Name</span>
                    <span className="bg-[#8b2500] text-white text-[10px] px-2 py-0.5 rounded">必須</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#8b2500] rounded-l-lg" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="山田 太郎"
                      className="w-full pl-5 pr-4 py-3 rounded-lg border-2 border-stone-200 focus:border-[#8b2500] focus:ring-2 focus:ring-[#8b2500]/20 outline-none transition-all text-sm bg-white"
                      style={{ fontFamily: "'Noto Serif JP', serif" }}
                    />
                  </div>
                </div>

                {/* メールアドレス */}
                <div>
                  <label
                    className="flex items-center justify-between text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide"
                    style={{ fontFamily: "'Noto Serif JP', serif" }}
                  >
                    <span>メールアドレス / Email</span>
                    <span className="bg-[#8b2500] text-white text-[10px] px-2 py-0.5 rounded">必須</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#8b2500] rounded-l-lg" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="example@email.com"
                      className="w-full pl-5 pr-4 py-3 rounded-lg border-2 border-stone-200 focus:border-[#8b2500] focus:ring-2 focus:ring-[#8b2500]/20 outline-none transition-all text-sm bg-white"
                      style={{ fontFamily: "'Noto Serif JP', serif" }}
                    />
                  </div>
                </div>

                {/* 物件所在地 */}
                <div>
                  <label
                    className="flex items-center justify-between text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide"
                    style={{ fontFamily: "'Noto Serif JP', serif" }}
                  >
                    <span>物件所在地 / Property Location</span>
                    <span className="bg-[#8b2500] text-white text-[10px] px-2 py-0.5 rounded">必須</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#c9a84c] rounded-l-lg" />
                    <input
                      type="text"
                      name="propertyLocation"
                      value={formData.propertyLocation}
                      onChange={handleChange}
                      required
                      placeholder="例：東京都渋谷区"
                      className="w-full pl-5 pr-4 py-3 rounded-lg border-2 border-stone-200 focus:border-[#8b2500] focus:ring-2 focus:ring-[#8b2500]/20 outline-none transition-all text-sm bg-white"
                      style={{ fontFamily: "'Noto Serif JP', serif" }}
                    />
                  </div>
                </div>

                {/* 物件タイプ */}
                <div>
                  <label
                    className="flex items-center justify-between text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide"
                    style={{ fontFamily: "'Noto Serif JP', serif" }}
                  >
                    <span>物件タイプ / Property Type</span>
                    <span className="bg-[#8b2500] text-white text-[10px] px-2 py-0.5 rounded">必須</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#c9a84c] rounded-l-lg" />
                    <select
                      name="propertyType"
                      value={formData.propertyType}
                      onChange={handleChange}
                      required
                      className="w-full pl-5 pr-4 py-3 rounded-lg border-2 border-stone-200 focus:border-[#8b2500] focus:ring-2 focus:ring-[#8b2500]/20 outline-none transition-all text-sm bg-white cursor-pointer"
                      style={{ fontFamily: "'Noto Serif JP', serif" }}
                    >
                      <option value="">選択してください</option>
                      <option value="マンション（1K〜1LDK）">マンション（1K〜1LDK）</option>
                      <option value="マンション（2LDK以上）">マンション（2LDK以上）</option>
                      <option value="一戸建て">一戸建て</option>
                      <option value="古民家・戸建て">古民家・戸建て</option>
                      <option value="その他">その他</option>
                    </select>
                  </div>
                </div>

                {/* ご相談内容 */}
                <div>
                  <label
                    className="flex items-center justify-between text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide"
                    style={{ fontFamily: "'Noto Serif JP', serif" }}
                  >
                    <span>ご相談内容</span>
                    <div className="flex items-center gap-2">
                      <span className="bg-[#8b2500] text-white text-[10px] px-2 py-0.5 rounded">必須</span>
                      <span className={`text-[10px] ${formData.message.length > 500 ? 'text-red-500' : 'text-slate-400'}`}>
                        {formData.message.length}/500
                      </span>
                    </div>
                  </label>
                  <div className="relative">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#8b2500] rounded-l-lg" />
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="物件の場所・現在の状況・ご希望などをご記入ください（500文字以内）"
                      className="w-full pl-5 pr-4 py-3 rounded-lg border-2 border-stone-200 focus:border-[#8b2500] focus:ring-2 focus:ring-[#8b2500]/20 outline-none transition-all resize-none text-sm bg-white"
                      style={{ fontFamily: "'Noto Serif JP', serif" }}
                    />
                  </div>
                </div>

                {/* 区切り線 */}
                <div className="h-px bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" />

                {/* ステータスメッセージ */}
                {submitStatus === 'error' && (
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 border-2 border-red-400">
                    <i className="ri-error-warning-line text-lg text-red-600 flex-shrink-0 mt-0.5"></i>
                    <p className="text-sm text-red-800" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                      送信に失敗しました。お手数ですが、もう一度お試しください。
                    </p>
                  </div>
                )}

                {/* 送信ボタン */}
                <button
                  type="submit"
                  disabled={isSubmitting || formData.message.length > 500}
                  className="w-full flex items-center justify-center gap-2 py-4 rounded-lg font-bold text-white transition-all hover:shadow-xl hover:shadow-[#8b2500]/30 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg whitespace-nowrap"
                  style={{
                    background: 'linear-gradient(90deg, #8b2500, #a52a00, #8b2500)',
                    fontFamily: "'Noto Serif JP', serif",
                  }}
                >
                  <span className="text-[#c9a84c]">✦</span>
                  <i className="ri-send-plane-fill text-lg"></i>
                  {isSubmitting ? '送信中...' : '無料相談を申し込む'}
                  <span className="text-[#c9a84c]">✦</span>
                </button>

                {/* 区切り */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent to-[#c9a84c]/50" />
                  <span className="text-xs text-slate-500 font-bold" style={{ fontFamily: "'Noto Serif JP', serif" }}>または直接ご連絡</span>
                  <div className="flex-1 h-px bg-gradient-to-l from-transparent to-[#c9a84c]/50" />
                </div>

                {/* WhatsApp・LINEボタン */}
                <div className="grid grid-cols-2 gap-3">
                  <a
                    href="https://wa.me/819012345678"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 py-3 rounded-lg font-bold text-white transition-all hover:opacity-90 hover:-translate-y-0.5 shadow-md whitespace-nowrap cursor-pointer"
                    style={{ background: 'linear-gradient(135deg, #25D366, #1ebe5d)', fontFamily: "'Noto Serif JP', serif" }}
                  >
                    <i className="ri-whatsapp-line text-xl"></i>
                    <span className="text-sm">WhatsApp</span>
                  </a>
                  <a
                    href="https://line.me/R/ti/p/@yourlineid"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 py-3 rounded-lg font-bold text-white transition-all hover:opacity-90 hover:-translate-y-0.5 shadow-md whitespace-nowrap cursor-pointer"
                    style={{ background: 'linear-gradient(135deg, #06C755, #05b34d)', fontFamily: "'Noto Serif JP', serif" }}
                  >
                    <i className="ri-chat-smile-2-line text-xl"></i>
                    <span className="text-sm">LINE</span>
                  </a>
                </div>

                <div className="h-px bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" />

                <p className="text-center text-xs text-slate-500" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  ご予約確認後、担当者よりご連絡いたします
                </p>

                {/* お支払い方法 */}
                <div
                  className="rounded-xl p-4"
                  style={{ background: 'linear-gradient(135deg, #fff8e1 0%, #fef3e2 100%)', border: '2px solid #c9a84c' }}
                >
                  <p
                    className="text-xs font-bold text-center mb-3"
                    style={{ color: '#8b2500', fontFamily: "'Noto Serif JP', serif" }}
                  >
                    ◆ ご利用可能なお支払い方法 ◆
                  </p>
                  <div className="flex items-center justify-center gap-3 flex-wrap">
                    <div className="flex items-center justify-center px-3 py-1.5 rounded-md bg-white shadow-sm border border-gray-200" style={{ minWidth: 56 }}>
                      <span className="font-extrabold text-sm tracking-tight" style={{ color: '#1A1F71', fontFamily: 'Arial, sans-serif' }}>VISA</span>
                    </div>
                    <div className="flex items-center justify-center gap-1 px-3 py-1.5 rounded-md bg-white shadow-sm border border-gray-200" style={{ minWidth: 56 }}>
                      <div className="w-5 h-5 rounded-full" style={{ background: '#EB001B' }}></div>
                      <div className="w-5 h-5 rounded-full -ml-2.5" style={{ background: '#F79E1B', opacity: 0.9 }}></div>
                    </div>
                    <div className="flex items-center justify-center px-3 py-1.5 rounded-md bg-white shadow-sm border border-gray-200" style={{ minWidth: 56 }}>
                      <span className="font-extrabold text-sm" style={{ background: 'linear-gradient(135deg, #003087, #009f6b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontFamily: 'Arial, sans-serif' }}>JCB</span>
                    </div>
                    <div className="flex items-center justify-center px-3 py-1.5 rounded-md bg-white shadow-sm border border-gray-200" style={{ minWidth: 56 }}>
                      <span className="font-extrabold text-xs tracking-wide" style={{ color: '#007BC1', fontFamily: 'Arial, sans-serif' }}>AMEX</span>
                    </div>
                  </div>
                  <p className="text-xs text-center mt-2" style={{ color: '#9a7a50', fontFamily: "'Noto Serif JP', serif" }}>
                    クレジットカード決済に対応しています
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* 下部信頼テキスト */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-wrap items-center justify-center gap-6 text-sm text-slate-400">
            <div className="flex items-center gap-2">
              <i className="ri-shield-check-line text-[#c9a84c] text-lg"></i>
              <span style={{ fontFamily: "'Noto Serif JP', serif" }}>個人情報は厳重に管理</span>
            </div>
            <div className="w-px h-4 bg-[#c9a84c]/40"></div>
            <div className="flex items-center gap-2">
              <i className="ri-time-line text-[#c9a84c] text-lg"></i>
              <span style={{ fontFamily: "'Noto Serif JP', serif" }}>2営業日以内に返信</span>
            </div>
            <div className="w-px h-4 bg-[#c9a84c]/40"></div>
            <div className="flex items-center gap-2">
              <i className="ri-phone-line text-[#c9a84c] text-lg"></i>
              <span style={{ fontFamily: "'Noto Serif JP', serif" }}>電話相談も可能</span>
            </div>
          </div>
        </div>
      </div>

      {/* 下部装飾ライン */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-1">
        {[...Array(20)].map((_, i) => (
          <div key={i} className={`w-3 h-3 ${i % 2 === 0 ? 'bg-[#8b2500]' : 'bg-[#c9a84c]'} opacity-60`} />
        ))}
      </div>
      <div className="absolute bottom-4 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-3 bg-gradient-to-r from-transparent via-[#8b2500] to-transparent opacity-80" />
    </section>
  );
}
